import 'react-toastify/dist/ReactToastify.css';
import '../public/fonts/fonts.css';
import '@adyen/adyen-web/dist/adyen.css';

import { ApolloProvider, useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { AppContext } from 'next/app';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { parseCookies } from 'nookies';
import * as R from 'ramda';
import React, { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { ToastContainer } from 'react-toastify';
import { ThemeProvider } from 'styled-components';

import LoadingSpinner from '@rebound/design-system/components/LoadingSpinner';
import { GlobalStyle, theme } from '@rebound/design-system/theme/styledTheme';
import { UserJourneyEnum } from '@rebound/shared-models/retailer/retailer.model';
import { getSafeLanguage } from '@rebound/shared-models/utils';

import { ErrorFallback } from '@components/ErrorFallback';
import { sentryConfig } from '@config';
import { PortalDisabledView } from '@modules/retailer/components/PortalDisabledView';
import { RetailerContext } from '@modules/retailer/retailer.context';
import {
  GetRetailerBySubdomainQuery,
  GetRetailerBySubdomainQueryResult,
} from '@modules/retailer/retailer.queries';
import {
  getSubdomainFromContext,
  getSubdomainFromLocation,
} from '@modules/retailer/retailer.utils';
import { useApollo } from '@services/apollo.service';
import i18n, { i18nErrorCallback } from '@services/i18n.service';

/**
 * WDYR (why-did-you-render) helps locate unnecessary re-renders and fix them
 * Applied in development environment, on the frontend only
 *
 * @see https://github.com/welldone-software/why-did-you-render
 */
// if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
//   // eslint-disable-next-line @typescript-eslint/no-var-requires
//   const whyDidYouRender = require('@welldone-software/why-did-you-render');
//   // eslint-disable-next-line no-console
//   console.debug(
//     'Applying whyDidYouRender, to help you locate unnecessary re-renders during development. See https://github.com/welldone-software/why-did-you-render',
//   );
//   whyDidYouRender(React, {
//     trackAllPureComponents: true,
//   });
// }

const isBrowser = typeof window !== 'undefined';
if (isBrowser) {
  Sentry.init(sentryConfig);
}

const unauthEmailRoutes = ['/', '/search-order', '/create-return'];
const unauthOrderRoutes = ['/', '/dashboard/orders'];
const routesWithoutAccessNeeded = [
  '/track-return',
  '/privacy-policy',
  '/terms-and-conditions',
];

const checkIsUnauthenticatedRoute = (
  r: string,
  isEmailVerificationJourney: boolean,
) =>
  (isEmailVerificationJourney && unauthEmailRoutes.indexOf(r) !== -1) ||
  (!isEmailVerificationJourney && unauthOrderRoutes.indexOf(r) !== -1);
const checkIfAccessToRouteIsRequired = (r: string) =>
  routesWithoutAccessNeeded.includes(r);

type AppProps = {
  Component: any;
  pageProps: any;
  store: any;
  apolloClient: any;
  router: any;
};

export function CustomApp({ Component, pageProps }: AppProps) {
  const apolloClient = useApollo(pageProps);
  const router = useRouter();
  const { data, loading } = useQuery<GetRetailerBySubdomainQueryResult>(
    GetRetailerBySubdomainQuery,
    {
      client: apolloClient,
      variables: { subdomain: pageProps.subdomain },
    },
  );
  const [isLoading, setIsLoading] = useState(true);
  const userJourney =
    data?.getRetailerBySubdomain.userJourneyRadio ??
    UserJourneyEnum.EMAIL_VERIFICATION;

  const isEmailVerificationJourney =
    userJourney === UserJourneyEnum.EMAIL_VERIFICATION;
  const isRoWithoutSoJourney =
    userJourney === UserJourneyEnum.RETURN_WITHOUT_SALES_ORDER;
  const isOrderLookupJourney = userJourney === UserJourneyEnum.ORDER_LOOKUP;

  const isPortalDisabled = !!data?.getRetailerBySubdomain?.portalDisabled;

  const handleRouting = (path: string) => {
    router.push(path).then(() => setIsLoading(false));
  };

  const getHomePath = () => {
    if (isEmailVerificationJourney) {
      return '/';
    } else {
      return isRoWithoutSoJourney ? '/create-return' : '/search-order';
    }
  };

  useEffect(() => {
    if (!router.isReady) return;
    const token = localStorage.getItem('token');
    const isUnauthenticatedRoute = checkIsUnauthenticatedRoute(
      router.pathname,
      isEmailVerificationJourney,
    );
    const isUnrestrictedRoute = checkIfAccessToRouteIsRequired(router.pathname);
    if (!loading && isPortalDisabled) {
      setIsLoading(false);
    } else if (!loading) {
      const homePath = getHomePath();
      if (isEmailVerificationJourney) {
        if (token) {
          if (isUnauthenticatedRoute && isLoading) {
            handleRouting('/dashboard/orders');
          } else {
            setIsLoading(false);
          }
        } else {
          if (router.pathname === homePath) {
            setIsLoading(false);
          } else {
            if (isUnrestrictedRoute) {
              setIsLoading(false);
            } else {
              handleRouting(homePath);
            }
          }
        }
      } else if (isUnauthenticatedRoute && isLoading) {
        handleRouting(homePath);
      } else if (isRoWithoutSoJourney && router.pathname === '/search-order') {
        handleRouting(homePath);
      } else if (isOrderLookupJourney && router.pathname === '/create-return') {
        handleRouting(homePath);
      } else {
        setIsLoading(false);
      }
    }
  }, [
    data?.getRetailerBySubdomain.userJourneyRadio,
    isLoading,
    loading,
    isPortalDisabled,
    router.isReady,
    router.pathname,
  ]);

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ApolloProvider client={apolloClient}>
      <>
        <NextSeo
          title="Retailer – Rebound Returns"
          description="Rebound Returns retailer portal."
          openGraph={{
            locale: 'en_US',
            title: 'Retailer – Rebound Returns',
            description: 'Rebound Returns retailer portal.',
            type: 'website',
          }}
        />
        <Head>
          {/* Use minimum-scale=1 to enable GPU rasterization */}
          <meta
            name="viewport"
            content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no"
          />
        </Head>

        <ToastContainer />

        <ErrorBoundary
          FallbackComponent={ErrorFallback}
          onError={(_error, info) => {
            if (typeof window !== 'undefined') {
              (window as any).__ERROR_COMPONENT_STACK__ = info.componentStack;
            }
          }}
          onReset={() => window.location.reload()}
        >
          <I18nextProvider i18n={i18n}>
            <ThemeProvider theme={Object.assign({}, theme, theme.breakpoints)}>
              <RetailerContext.Provider
                value={{ subdomain: pageProps.subdomain }}
              >
                <GlobalStyle />
                {isPortalDisabled ? (
                  <PortalDisabledView
                    message={
                      data?.getRetailerBySubdomain?.portalDisabledMessage
                    }
                  >
                    <Component {...pageProps} />
                  </PortalDisabledView>
                ) : (
                  <Component {...pageProps} />
                )}
              </RetailerContext.Provider>
            </ThemeProvider>
          </I18nextProvider>
        </ErrorBoundary>
      </>
    </ApolloProvider>
  );
}

CustomApp.getInitialProps = async (context: AppContext) => {
  const { ctx, Component } = context;
  const { req } = ctx;
  let pageProps: any = {};

  if (Component.getInitialProps) {
    pageProps = await Component.getInitialProps(ctx);
  }

  const locale = R.propOr(null, 'i18nextLocale', parseCookies(ctx));

  if (locale) {
    i18n.changeLanguage(locale as string, i18nErrorCallback);
  }

  if (req && req.headers['accept-language'] && !locale) {
    const supportedLocale = ctx.req?.headers['accept-language']?.slice(0, 2);
    i18n.changeLanguage(supportedLocale, i18nErrorCallback);
  }

  // fall back to en if language is undefined
  if (!getSafeLanguage(i18n)) {
    i18n.changeLanguage('en', i18nErrorCallback);
  }

  const subdomain = !isBrowser
    ? getSubdomainFromContext(ctx)
    : getSubdomainFromLocation();

  pageProps.subdomain = subdomain;

  return { pageProps };
};

export default CustomApp;
